import { configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import { DEFAULT_TDM_DATA } from '../../constants/tdm';
import type { typeTopicsData } from '../../constants/typeDefinitions/crTypes';
import type { SnackbarConfig } from '../../utility/services/customHooks/snackbar.types';
import type {
  KrAcceptedSlateQuality,
  KrAggregateNoLiftRescueSummary,
  KrBuildFingerprint,
  KrCompetitorGapPlan,
  KrDecisionPlan,
  KrHeadingOutlineAction,
  KrHeadingOutlinePlan,
  KrInputFreshnessAudit,
  KrIntentActionPlan,
  KrMetadataFluencyIssueSummary,
  KrMetadataFieldPolicy,
  KrEarlyNoLiftStopSummary,
  KrRewriteWarning,
  KrOpportunityCensus,
  KrPhraseSaturationSummary,
  KrStructurePreservationSummary,
} from '../../types/keywordRelevance';
import integrationsApi from '../services/api/integrations';
import schemaApi from '../services/api/schema';
import sessionApi from '../services/api/session';
import commonApi from '../services/api/common';
import { reducer } from './reducers';

export interface ContentRecommendation {
  projectedContentQualityScore: string;
  finalurl: string;
}
export const initialState = {
  lookerUserId: undefined,
  sidebarOpen: true,
  topTrendsList: [] as any[],
  showFullScreen: false,
  explores: [] as any[],
  looksList: [] as any[],
  dashboardsList: [] as any[] as any[],
  blobUrls: {},
  looker_permissions: [] as any[],
  snackConfig: {
    show: false,
    open: false,
    type: 'error' as const,
    text: 'Oops! Something Went Wrong. Please try again..',
  } as SnackbarConfig,
  adHocAnalysisFolders: [] as any[],
  personalFolders: [] as any[],
  personalFolderId: null as string | null,
  segmentFiltersData: {},
  loadersArray: [] as string[],
  competitorsData: {},
  drillDownRowData: {},
  topNData: [] as any[],
  gtSelectedFilters: {
    tempSegmentData: {
      segmentData_1: {},
      segmentData_2: {},
    },
  },
  stSelectedFilters: {
    tempSegmentData: {
      segmentData_1: {},
      segmentData_2: {},
    },
  },
  headerInfo: {
    title: '',
  },
  potentialOpportunityList: [] as any[],
  topOpportunityList: [] as any[],
  growthTrendsMarketShareFilters: {},
  datePickerFilter: {} as any,
  segment1_name: 'Primary Segment',
  segment2_name: 'Comparison Segment',
  usabilityRecommendationList: [] as any[],
  usabilityExportFilters: null as any,
  contentRecommendationList: [] as ContentRecommendation[],
  contentBriefList: [] as any[],
  customerPagesUploads: [] as any[],
  customerKeywordsUploads: [] as any[],
  slackChannels: [] as any[],
  customerInfo: [] as any[],
  userSelectedDateRange: {},
  defaultDateRange: {},
  summaryMetricData: {},
  drilldownCachedMetricData: {},
  recommendationFiltersData: {},
  contentRecommendationFiltersData: {} as Record<string, any>,
  usabilityScore: [] as any[],
  defaultDomain: '',
  defaultMarketSegment: undefined,
  confirmationModal: {},
  crDetailsModal: {},
  addPageToOptimizeModelForTrends: {},
  intentList: [] as any[],
  ruleList: [] as any[],
  entityList: [] as any[],
  ruleStoreList: [] as any[],
  intentAllSearchList: [] as any[],
  intentSummaryData: {
    monthly_avg_clicks: 0,
    monthly_avg_impressions: 0,
    monthly_avg_paid_clicks: 0,
    monthly_avg_paid_impressions: 0,
    no_of_keywords: 0,
  },
  intentAllSearchSummaryData: {
    monthly_avg_clicks: 0,
    monthly_avg_impressions: 0,
    monthly_avg_paid_clicks: 0,
    monthly_avg_paid_impressions: 0,
    no_of_keywords: 0,
  },
  categoryList: [] as any[],
  categorySummaryData: {
    monthly_avg_clicks: 0,
    monthly_avg_impressions: 0,
    monthly_avg_paid_clicks: 0,
    monthly_avg_paid_impressions: 0,
    no_of_keywords: 0,
    no_of_pages: 0,
  },
  categoryAllSearchList: [] as any[],
  categoryAllSearchSummaryData: {
    monthly_avg_clicks: 0,
    monthly_avg_impressions: 0,
    monthly_avg_paid_clicks: 0,
    monthly_avg_paid_impressions: 0,
    no_of_keywords: 0,
    no_of_pages: 0,
  },
  categoryPageList: [] as any[],
  // customerPreviewHtml: {},
  showCrFullScreenSpinner: false, // BEFORE CALL NEW ACTION, HOLD THE USER TO UPDATE ALL THE SCORES AFTER THE RESPONSE of ADD/UPDATE/DELETE ACTION
  ////-------------CR RELATED STATES-------
  crPageTagsHtmlDataCollection: {}, // like h1:[{all h1 data of page}], h2:[{all h2 data of page}], p:[]
  keywordsData: [] as any[], // all Targeted keywords
  cikKeywordsData: [] as any[],
  pageAllHtmlData: [] as any[],
  allCompetitorsHtmlData: [] as any[],
  topicsData: {
    ...DEFAULT_TDM_DATA,
  } as unknown as typeTopicsData,
  potentialKeywordsData: [] as any[],
  aiKeywordsData: [] as any[],
  combinedKeywordsData: [] as any[],
  isTriggerIncludedPotentialKeyword: false,
  masterLanguageAndCountry: {},
  globalAutoWriterMagicPin: {
    open: false,
    defaultText: '',
  },
  contentBriefTinyMceData: {
    initialLoad: true,
    contentBriefString: '',
    orderdExtractedEleIds: [] as any[],
  },
  gptStreamingData: {
    text: '',
    isHtmlMarkup: false,
  },
  gptCompleteContent: {
    content: '',
    timestamp: 0,
  },
  gptThinkingState: {
    isThinking: false,
    status: '',
    message: '',
  },
  gptThinkingContent: '',
  gptPersistedThinkingContent: {
    content: '',
    timestamp: 0,
    promptInfo: {
      promptFor: '',
      keyword: '',
      title: '',
    },
  },
  wsInstance: null as any,
  elementMappingDetails: undefined as any | undefined,
  elementMappingDetailsCompetitor: undefined as any | undefined,
  showSubscriberBanner: false as boolean | string,
  subscriberMeta: null as any,
  creditsRemaining: 0 as any,
  dayPassCreditsRemaining: 0 as any,
  creditsUsagePerDay: 0 as any,
  customPopupConfig: {
    showPopup: false,
    text: '',
    anchorText: '',
    anchorLink: '',
  },
  autoNomousInternalLinkingDetails: {
    status: '',
    list: [],
  },
  pageDraftHtmlContent: {
    html: '',
    json: {},
    orderdExtractedEleIds: [] as any[],
  },
  tempModifiedHtmlContent: {
    html: '',
    json: {},
  },
  gigaDraftContent: {
    //// this is for giga draft putput
    html: '',
    json: {},
  },
  tempGigaReviewContent: {
    ////this is for giga draft output modification from user for temp purpose
    html: '',
    json: {},
    isTemp: false,
  },
  // GIGA conflict resolution snapshot and detected conflicts
  gigaBaseSnapshot: {
    html: '',
    json: {},
    timestamp: 0,
  },
  gigaConflicts: [] as any[],
  // GIGA — Boost Keyword Relevance (KR)
  gigaKeywordBoost: {
    gapAnalysis: null as null | {
      targetTagGroups: string[];
      perTagGroup: Record<
        string,
        {
          customerScore: number;
          competitorBest: number;
          gap: number;
          contributionWeight: number;
          slotBudget: number;
        }
      >;
      baselineProjectedKas: number;
      timestamp: number;
    },
    elements: {} as Record<
      string,
      {
        elementId: string | number;
        tagGroup: string;
        elementTag?: string;
        iteration: number;
        beforeScore: number;
        afterScore: number;
        displayBeforeScore?: number;
        displayAfterScore?: number;
        perKeywordElementScores?: Record<string, { before: number; after: number; delta: number }>;
        accepted: boolean;
        internalOnly?: boolean;
        rejectionReason?:
          | 'delta_below_threshold'
          | 'identity_noop'
          | 'regression'
          | 'invalid_output'
          | 'reasoning_leak'
          | 'judge_grammar'
          | 'judge_factuality'
          | 'judge_metadata_quality'
          | 'judge_section_quality'
          | 'judge_heading_quality'
          | 'aggregate_no_lift'
          | 'protected_keyword_weighted_regression'
          | 'sibling_redundant'
          | 'batch_keyword_grafting'
          | 'batch_repetitive_phrase'
          | 'batch_no_page_lift'
          | 'batch_editorial_judge'
          | 'structure_preservation_failed'
          | 'superseded_same_element_rewrite'
          | 'skip_unrewritable'
          | 'locked_field_surfaced';
        /** Soft batch-gate verdict on an APPLIED rewrite — review chip + the existing one-click Reject. */
        warning?: KrRewriteWarning;
        /**
         * Suggested-not-applied candidate (e.g. policy-locked title rewrite).
         * Synthetic elementIds (`locked:title:123`, `surfaced:456`) do NOT map
         * to DOM nodes — read-only suggestion, never auto-applied.
         */
        surfaced?: boolean;
        surfacedReason?: string;
        competitorBest: number;
        original: string;
        rewritten: string;
        rejected?: boolean;
        timestamp: number;
        operationType?: 'element-rewrite' | 'section-add' | 'section-split' | 'heading-add' | 'body-span-rewrite';
        applicationMode?: string;
        sectionPayload?: {
          heading: string;
          paragraphs: string[];
          insertedAfterH2Id?: string | number;
          targetKeyword?: string;
          newElementIds?: Array<string | number>;
        } | null;
        splitPayload?: {
          originalParagraph: string;
          heading: string;
          firstPart: string;
          secondPart: string;
          paragraphId?: string | number;
          newElementIds?: Array<string | number>;
        } | null;
        headingAddPayload?: {
          heading: string;
          headingLevel: 'H2' | 'H3';
          insertBeforeElementId: string | number;
          parentHeadingId?: string | number | null;
          parentHeadingText?: string;
          coveredElementIds: Array<string | number>;
          targetKeyword: string;
          newElementId: string | number;
        } | null;
        bodySpanPayload?: {
          targetKeyword: string;
          parentHeadingId?: string | number | null;
          parentHeadingText?: string;
          elementIds: Array<string | number>;
          originalTexts: string[];
          rewrittenTexts: string[];
        } | null;
      }
    >,
    iterations: [] as Array<{
      iteration: number;
      acceptedCount: number;
      rejectedCount: number;
      projectedKasBefore: number;
      projectedKasAfter: number;
      perTagGroupBeforeAfter: Record<string, { before: number; after: number; competitorBest: number }>;
      competitorGapRemaining: Record<string, number>;
      timestamp: number;
    }>,
    completion: null as null | {
      totalIterations: number;
      finalProjectedKas: number;
      baselineProjectedKas: number;
      success: boolean;
      stopReason: string;
      outcomeStatus?: string;
      rawOutcomeStatus?: string;
      officialOutcomeStatus?: string;
      dominantBlocker?: string;
      outcomeEvidence?: string[];
      rawKasDelta?: number;
      officialKasDelta?: number;
      rawKrRewriteLift?: number;
      officialPipelineLift?: number;
      krAttributedLift?: number;
      meaningfulLift?: boolean;
      acceptedButNoOfficialLift?: boolean;
      s3ReportKey?: string;
      acceptedRewriteCount?: number;
      acceptedVisibleRewriteCount?: number;
      krAlgorithmVersion?: string;
      buildFingerprint?: KrBuildFingerprint | null;
      krDecisionPlan?: KrDecisionPlan | null;
      krIntentActionPlan?: KrIntentActionPlan | null;
      krHeadingOutlinePlan?: KrHeadingOutlinePlan | null;
      headingOutlineActions?: KrHeadingOutlineAction[];
      timestamp: number;
      perKeywordTagGroupBefore?: Record<string, Record<string, number>>;
      perKeywordTagGroupAfter?: Record<string, Record<string, number>>;
      comparablePerKeywordTagGroupBefore?: Record<string, Record<string, number>>;
      comparablePerKeywordTagGroupAfter?: Record<string, Record<string, number>>;
      perKeywordTagGroupCompetitor?: Record<string, Record<string, number>>;
      perKeywordElementScoreBefore?: Record<string, Record<string, number>>;
      perKeywordElementScoreAfter?: Record<string, Record<string, number>>;
      perKeywordKesBefore?: Record<string, number>;
      perKeywordKesAfter?: Record<string, number>;
      comparablePerKeywordKesBefore?: Record<string, number>;
      comparablePerKeywordKesAfter?: Record<string, number>;
      perKeywordScoreProvenance?: {
        version?: string;
        comparableSource?: string;
        comparableScope?: string;
        officialSnapshotSource?: string;
      } | null;
      competitorGapPlan?: KrCompetitorGapPlan | null;
      inputFreshnessAudit?: KrInputFreshnessAudit | null;
      metadataFieldPolicy?: KrMetadataFieldPolicy | null;
      aggregateNoLiftRescue?: KrAggregateNoLiftRescueSummary | null;
      metadataFluencyIssues?: KrMetadataFluencyIssueSummary | null;
      acceptedSlateQuality?: KrAcceptedSlateQuality | null;
      structurePreservationSummary?: KrStructurePreservationSummary | null;
      phraseSaturation?: KrPhraseSaturationSummary | null;
      earlyNoLiftStop?: KrEarlyNoLiftStopSummary | null;
      repetitionPreventedCount?: number;
      opportunityCensus?: KrOpportunityCensus | null;
      advisory?: {
        summary: string;
        estimatedKasLiftIfApplied: number;
        keywordConsolidation: Array<{
          cluster: string[];
          primary: string;
          totalKis: number;
          reason: string;
        }>;
        tagGroupGaps: Array<{
          tagGroup: string;
          customer: number;
          competitor: number;
          estimatedKasLift: number;
          message: string;
          actionHint:
            | 'add_alt_text'
            | 'add_internal_links'
            | 'add_schema'
            | 'improve_eeat'
            | 'review_keyword_targeting';
        }>;
        gapExplanations?: Array<{
          tagGroup: string;
          keyword?: string;
          title: string;
          explanation: string;
          nextStep: string;
          competitorEvidence?: Array<{
            url?: string;
            headingTag?: string;
            heading?: string;
            title?: string;
            description?: string;
            text?: string;
          }>;
          source?: 'llm' | 'fallback';
          gapDisposition?: 'actionable' | 'justified' | 'near_ceiling' | 'positioning_ceiling';
          displaySeverity?: 'red' | 'amber' | 'neutral';
          displayTitle?: string;
          displayLabel?: string;
          displayNextStep?: string;
          shouldCountAsRemainingOpportunity?: boolean;
        }>;
        keywordTargetingIssues?: Array<{
          issueType: 'low_keyword_fit' | 'low_metadata_fit';
          tagGroup: 'all' | 'meta';
          estimatedKasLift?: number;
          keywords: Array<{
            keyword: string;
            customer: number;
            competitor: number;
            gap: number;
            tagGroupGaps?: Record<string, number>;
            weightedRelevanceGap?: number;
            kis: number;
            fitScore: number;
            reason: string;
            estimatedKasLift?: number;
          }>;
          message: string;
          recommendation: string;
        }>;
        keywordUsageNotes?: Array<{
          issueType: 'partial_keyword_fit' | 'metadata_only_mismatch';
          tagGroup: 'all' | 'meta';
          keywords: Array<{
            keyword: string;
            customer: number;
            competitor: number;
            gap: number;
            tagGroupGaps?: Record<string, number>;
            weightedRelevanceGap?: number;
            kis: number;
            fitScore: number;
            reason: string;
            safeForMetadata: boolean;
            safeForRewrites: boolean;
          }>;
          message: string;
          recommendation: string;
        }>;
        suggestedKeywords?: Array<{
          keyword: string;
          reason: string;
          source: 'page_url' | 'metadata_intent' | 'page_topic' | 'excluded_cluster';
          fitDecision?: 'accepted' | 'rejected';
          fitReason?: string;
          specificityScore?: number;
          sourceCluster?: string;
          replaces?: string[];
          clusterId?: string | number;
          clusterName?: string;
          keywordImportance?: number;
        }>;
      };
      optimizationEffort?: null | {
        totalCandidates: number;
        acceptedCandidates: number;
        acceptedVisibleCandidates: number;
        rejectedCandidates: number;
        qualityRejected: number;
        scoringRejected: number;
        scopeRejected: number;
        stopReason: string;
        summary: string;
        rejectionReasons: Array<{
          reason: string;
          count: number;
          label: string;
          description: string;
          category: 'quality' | 'scoring' | 'scope';
        }>;
        remainingGapReasons: Array<{
          tagGroup: string;
          customer: number;
          competitor: number;
          gap: number;
          reason: string;
          recommendation: string;
          closability?: 'copy_editable' | 'positioning_ceiling' | 'asset_bounded' | 'capability_missing';
        }>;
        debugNotes?: string[];
      };
    },
    baselineByKeyword: {
      customer: {} as Record<string, Record<string, number>>,
      competitorBest: {} as Record<string, Record<string, number>>,
      customerScoreBasis: 'unknown' as 'canonical' | 'kr_stage' | 'unknown',
    },
  },
  // GIGA — Pipeline v2 (CQ + Links + KR attribution, per-flow deltas).
  // Hydrated on page mount from /giga-pipeline/:pageId and updated in
  // real time by the CHECKPOINT_RESCORED streaming event.
  gigaPipeline: null as null | {
    schemaVersion?: 'v2';
    selectedOptions: string[];
    workflowSteps: string[];
    checkpoints: Array<{
      stage: 'baseline' | 'post_cq' | 'post_links' | 'post_kr';
      projected_kas: number;
      overall_tag_group_score: Record<string, number>;
      overall_element_score?: Record<string, number>;
      per_keyword_tag_group_score: Record<string, Record<string, number>>;
      timestamp: number;
      skipped: boolean;
    }>;
    perFlowContributions: Array<{
      flow: 'cq' | 'links' | 'kr';
      overall_kas_delta: number;
      primary_metric?: 'relevance' | 'content_quality';
      primary_metric_delta?: number;
      per_keyword_overall_delta: Record<string, number>;
      per_keyword_tag_group_delta: Record<string, Record<string, number>>;
    }>;
    flowArtifacts: null | {
      links?: {
        addedCount?: number;
        acceptedCount?: number;
      };
    };
    officialScores: null | {
      relevance?: { baseline: number; final: number; delta: number };
      contentQuality?: { baseline: number; final: number; delta: number };
    };
    advisory: null | {
      summary: string;
      estimatedKasLiftIfApplied: number;
      keywordConsolidation: Array<{
        cluster: string[];
        primary: string;
        totalKis: number;
        reason: string;
      }>;
      tagGroupGaps: Array<{
        tagGroup: string;
        customer: number;
        competitor: number;
        estimatedKasLift: number;
        message: string;
        actionHint: 'add_alt_text' | 'add_internal_links' | 'add_schema' | 'improve_eeat' | 'review_keyword_targeting';
      }>;
      gapExplanations?: Array<{
        tagGroup: string;
        keyword?: string;
        title: string;
        explanation: string;
        nextStep: string;
        competitorEvidence?: Array<{
          url?: string;
          headingTag?: string;
          heading?: string;
          title?: string;
          description?: string;
          text?: string;
        }>;
        source?: 'llm' | 'fallback';
        gapDisposition?: 'actionable' | 'justified' | 'near_ceiling' | 'positioning_ceiling';
        displaySeverity?: 'red' | 'amber' | 'neutral';
        displayTitle?: string;
        displayLabel?: string;
        displayNextStep?: string;
        shouldCountAsRemainingOpportunity?: boolean;
      }>;
      keywordTargetingIssues?: Array<{
        issueType: 'low_keyword_fit' | 'low_metadata_fit';
        tagGroup: 'all' | 'meta';
        estimatedKasLift?: number;
        keywords: Array<{
          keyword: string;
          customer: number;
          competitor: number;
          gap: number;
          tagGroupGaps?: Record<string, number>;
          weightedRelevanceGap?: number;
          kis: number;
          fitScore: number;
          reason: string;
          estimatedKasLift?: number;
        }>;
        message: string;
        recommendation: string;
      }>;
      keywordUsageNotes?: Array<{
        issueType: 'partial_keyword_fit' | 'metadata_only_mismatch';
        tagGroup: 'all' | 'meta';
        keywords: Array<{
          keyword: string;
          customer: number;
          competitor: number;
          gap: number;
          tagGroupGaps?: Record<string, number>;
          weightedRelevanceGap?: number;
          kis: number;
          fitScore: number;
          reason: string;
          safeForMetadata: boolean;
          safeForRewrites: boolean;
        }>;
        message: string;
        recommendation: string;
      }>;
      suggestedKeywords?: Array<{
        keyword: string;
        reason: string;
        source: 'page_url' | 'metadata_intent' | 'page_topic' | 'excluded_cluster';
        fitDecision?: 'accepted' | 'rejected';
        fitReason?: string;
        specificityScore?: number;
        sourceCluster?: string;
        replaces?: string[];
        clusterId?: string | number;
        clusterName?: string;
        keywordImportance?: number;
      }>;
      missingFlowOpportunities: Array<{
        flow: 'boostContentQuality' | 'incorporateRelevantLinks' | 'boostKeywordRelevance';
        reason: string;
        estimatedKasLift: number;
        signals: Array<{
          type: 'large_tag_group_gap' | 'topic_coverage_gap' | 'weak_internal_linking';
          detail: string;
        }>;
      }>;
    };
    pageUrl: string;
    krReportKey?: string;
    /** Used by the fallback banner when the backend returned a legacy-only KR report. */
    legacy?: boolean;
  },
  editorType: '',
  shortcodes: [] as any[],
  wpPostType: 'post' as string,
  cmsPageId: null as string | null,
  cmsPagesPath: '',
  // Customer-configured WordPress custom fields (Banner Title / description, Key Takeaways, …)
  customFieldsConfig: [] as Array<{ key: string; label: string; type: 'text' | 'wysiwyg'; source?: string }>,
  customFieldsConfigFetched: false,
  customFieldsValues: {} as Record<string, string>,
  // Bookkeeping for the value rules in utility/customFieldsState.ts (user edits vs draft vs post)
  customFieldsDirty: {} as Record<string, true>,
  customFieldsSeedSource: {} as Record<string, 'post' | 'draft'>,
  customFieldsDraftUnpublished: {} as Record<string, true>,
  isAnyDirtyModificationInHtmlContent: false,
  isOutlineOpen: false,
  usersList: [] as any[],
  // Edit lock state
  editLockState: null as {
    isOwner: boolean;
    owner: { email: string; name: string } | null;
    expiresAt: number | null;
    mode: 'edit' | 'view';
    editLockEnabled?: boolean;
    error?: string;
  } | null,
  editLockRequests: [] as {
    requestId: string;
    requesterEmail: string;
    requesterName: string;
    createdAt: number;
    expiresAt: number;
  }[],
  // Taxonomy Designer state — persists across tab switches
  taxonomyDesigner: {
    session: null as any,
    messages: [] as any[],
    isStreaming: false,
    streamingError: null as string | null,
    model: [] as any[],
    quality: null as any,
    versions: [] as any[],
    currentVersion: null as string | null,
    selectedCategory: null as string | null,
    sampleUrls: [] as any[],
    samplingInfo: null as any,
    siteShape: [],
    savedDesign: null as any,
    classifyPagesLocked: false,
  },
  // Content Ideas
  contentIdeasTaskId: null as string | null,
  contentIdeasStatus: null as string | null,
  contentIdeasResults: null as any,
  contentIdeasSummary: null as any,
  contentIdeasSaved: [] as any[],
  contentIdeasCompetitors: [] as any[],
  contentIdeasTaskType: null as string | null,
  contentIdeasPlan: [] as any[],
};

export const initializeStore = (preloadedState?: Partial<InitialStateType>) => {
  const store = configureStore({
    reducer: {
      // Add the generated reducer as a specific top-level slice
      [sessionApi.reducerPath]: sessionApi.reducer,
      [integrationsApi.reducerPath]: integrationsApi.reducer,
      [schemaApi.reducerPath]: schemaApi.reducer,
      [commonApi.reducerPath]: commonApi.reducer,
      root: reducer,
    },
    preloadedState: { [sessionApi.reducerPath]: undefined, root: { ...initialState, ...preloadedState } },
    devTools: true,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: false,
        immutableCheck: false,
      })
        .concat(sessionApi.middleware)
        .concat(integrationsApi.middleware)
        .concat(schemaApi.middleware)
        .concat(commonApi.middleware),
  });

  setupListeners(store.dispatch);

  return store;
};

export const APP_STORE = initializeStore();

export type InitialStateType = typeof initialState;
export type InitialStoreType = ReturnType<typeof initializeStore>;
